// Agents — the Reverra Orchestrator → sub-agents + Learning Loop view.
//
// Leads with the orchestrator narrative the client was promised: one Main Agent
// coordinating the logical sub-agents (Email Triage, Meeting & Agreement,
// Focus-Time Guardian, Messages, Commission, Learning Loop). The 22 raw daemons
// roll up into those agents (LOGICAL_AGENTS); each agent's health is the rollup
// of its daemons' heartbeats and its "skills learned" comes from
// agent_learnings. The raw per-daemon ops view (restart, kill-switch, schema
// drift, token budget) is preserved under the collapsible Infrastructure detail.
//
// Read shape:
//   - app_settings rows keyed by `daemon_heartbeat:<name>` carry the
//     HeartbeatPayload that the daemon writes every ~30s via
//     startHeartbeat() from @reverra/shared/heartbeat.
//   - agent_learnings rows (id, agent, title, source, learned_at) feed both the
//     per-agent "skills learned" counts and the Learning Loop feed.
//   - app_settings rows keyed by `anthropic_tokens_used:<UTC-date>`
//     (renamed in W5 Pre-flight 1 from `email_triage_tokens_used:*`)
//     carry the daily SHARED token budget consumption — this is the
//     EMAIL_TRIAGE_DAILY_TOKEN_CAP fence shared by every Anthropic-call
//     worker (classify, draft, brain, agreement, focus, call-extract,
//     meeting-brief).
//
// Mutation shape: NONE. This is a read-only dashboard; the workers
// process owns all writes (heartbeats, watchdog, token accounting).
//
// Polling: 30s — matches the heartbeat write cadence, so a fresh tick
// is reflected within one refetch cycle.
//
// Screen field audit (per CLAUDE.md §19 standard review):
//   - daemon name           ← app_settings.key suffix after
//                             HEARTBEAT_KEY_PREFIX (writer: any caller of
//                             startHeartbeat — see workers/parsers/* and
//                             workers/agents/*)
//   - status pill           ← classifyHeartbeat(payload, now) — pure fn in
//                             packages/shared/src/heartbeat/read.ts
//   - last_seen_at          ← startHeartbeat writeOnce() per tick
//   - started_at            ← startHeartbeat at process start
//   - tick_count            ← startHeartbeat (incremented per tick)
//   - host                  ← startHeartbeat at process start (os.hostname)
//   - last_error/_at        ← startHeartbeat recordError() handle
//   - daily token spend     ← app_settings.anthropic_tokens_used:<date>
//                             (writer: agents/email/token-budget.ts:recordUsage;
//                             key renamed in W5 Pre-flight 1)
//   - daily token cap       ← EMAIL_TRIAGE_DAILY_TOKEN_CAP env (not surfaced
//                             via app_settings — see todo below; cap-aware
//                             rendering shows "—" until the env value is
//                             persisted somewhere readable from the browser)

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNowStrict } from 'date-fns';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Cpu,
  Database,
  DollarSign,
  FileSignature,
  HeartPulse,
  Loader2,
  Mail,
  MessageSquare,
  Monitor,
  Pause,
  RefreshCw,
  Server,
  Shield,
  Sparkles,
  Tag,
  TrendingUp,
  Zap,
  Moon,
} from 'lucide-react';
import {
  classifyHeartbeat,
  readAllHeartbeats,
  type HeartbeatPayload,
  type HeartbeatRow,
  type HeartbeatStatus,
} from '@reverra/shared/heartbeat/read';
import GlassCard from '@/components/shared/GlassCard';
import PersonaPanel from '@/components/agents/PersonaPanel';
import PageStatsRow from '@/components/shared/PageStatsRow';
import PulseDot from '@/components/shared/PulseDot';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { humanizeDaemonName } from '@/utils/humanizeDaemonName';
import { buildToggleStateMap, isDaemonPaused, TOGGLE_KEYS } from '@/utils/daemonKillSwitch';
import { useAuth } from '@/lib/auth';

const TOKEN_USED_KEY_PREFIX = 'anthropic_tokens_used:';

type TokenUsageRow = {
  used: number | null;
  key: string;
};

function todayUtcKey(now: Date = new Date()): string {
  const yyyy = now.getUTCFullYear().toString().padStart(4, '0');
  const mm = (now.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = now.getUTCDate().toString().padStart(2, '0');
  return `${TOKEN_USED_KEY_PREFIX}${yyyy}-${mm}-${dd}`;
}

async function fetchHeartbeats(): Promise<HeartbeatRow[]> {
  return readAllHeartbeats(supabase);
}

async function fetchToggles(): Promise<Array<{ key: string; value: string | null }>> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('key, value')
    .in('key', TOGGLE_KEYS as unknown as string[]);
  if (error) throw error;
  return data ?? [];
}

async function fetchTodayTokenUsage(): Promise<TokenUsageRow> {
  const key = todayUtcKey();
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', key)
    .maybeSingle();
  if (error) throw error;
  const raw = data?.value ? Number.parseInt(data.value, 10) : null;
  const used = raw !== null && Number.isFinite(raw) && raw >= 0 ? raw : null;
  return { used, key };
}

// 4th tier on top of HeartbeatStatus: a daemon whose kill-switch is OFF
// in Settings. The process is alive — the work is intentionally paused.
// Treated as a separate badge tone so the operator can see the toggle is
// reflected on /agents instead of the chip lying "alive" while the
// toggle says "off."
type AgentRowStatus = HeartbeatStatus | 'paused';

const STATUS_TONE: Record<
  AgentRowStatus,
  { badge: string; dot: 'green' | 'amber' | 'red' | 'blue' }
> = {
  alive: { badge: 'bg-forest-soft text-forest', dot: 'green' },
  warning: { badge: 'bg-attention/15 text-attention', dot: 'amber' },
  alert: { badge: 'bg-alert/15 text-alert', dot: 'red' },
  paused: { badge: 'bg-secondary text-muted-foreground', dot: 'blue' },
  // Distinct from 'paused' above: that is a kill-switch the operator turned
  // off; this is a capture daemon whose Mac is shut. Neither is a fault.
  asleep: { badge: 'bg-secondary text-muted-foreground', dot: 'blue' },
};

// Tone for a logical agent whose daemons aren't reporting yet — a neutral
// (blue) dot, never green, so "Idle" doesn't pair with a healthy-green dot.
const IDLE_TONE = { badge: 'bg-secondary text-muted-foreground', dot: 'blue' as const };

// ---------------------------------------------------------------------------
// Orchestrator framing — the 22 raw daemons roll up into the logical agents
// Joel was promised (CLAUDE.md §6). Each logical agent's health is the rollup
// of its daemons' heartbeats; its "skills learned" comes from agent_learnings.
// The Reverra Orchestrator (Main Agent) coordinates them all.
// ---------------------------------------------------------------------------

type LogicalAgent = {
  key: string;
  label: string;
  description: string;
  Icon: typeof Mail;
  daemons: string[];
  // Which agent_learnings.agent buckets feed this card's "skills learned".
  learningKeys: string[];
};

const LOGICAL_AGENTS: LogicalAgent[] = [
  {
    key: 'email',
    label: 'Email Triage',
    Icon: Mail,
    description:
      'Categorizes Gmail, drafts replies in your voice, flags urgent. Drafts only — you send.',
    daemons: [
      'workers_email_triage_classify',
      'workers_email_triage_draft',
      'workers_email_drafts_writeback',
      'workers_gmail_parser',
      'workers_gmail_watch_renewal',
    ],
    learningKeys: ['email'],
  },
  {
    key: 'meeting',
    label: 'Meeting & Agreement',
    Icon: FileSignature,
    description:
      'Pulls Fathom + call transcripts, writes pre-meeting briefs, extracts action items, redlines agreements.',
    daemons: [
      'workers_meeting_brief',
      'workers_fathom_parser',
      'workers_call_parser',
      'workers_call_extract_items',
      'workers_agreement_extract',
    ],
    learningKeys: ['meeting', 'agreement'],
  },
  {
    key: 'focus',
    label: 'Focus-Time Guardian',
    Icon: Shield,
    description: 'Defends your calendar focus blocks and proposes reschedules for conflicts.',
    daemons: ['workers_focus_guardian', 'workers_calendar_parser', 'workers_calendar_renewal'],
    learningKeys: ['focus'],
  },
  {
    key: 'messages',
    label: 'Messages',
    Icon: MessageSquare,
    description:
      'Reads iMessage / SMS / WhatsApp, digests your day, and mines commitments from your outgoing messages.',
    // Cloud-side processing daemons only. The Mac capture bridge
    // (bridge_imessage / bridge_whatsapp) is a separate hybrid component —
    // it appears under Infrastructure detail, not in this cloud-agent rollup.
    daemons: ['workers_imessage_parser', 'workers_message_digest', 'workers_message_extract_items'],
    learningKeys: ['messages'],
  },
  {
    key: 'commission',
    label: 'Commission',
    Icon: DollarSign,
    description: 'Merges Shopify + Excel + commission rules into a Gusto-ready export.',
    daemons: ['workers_commission_agent', 'workers_shopify_parser'],
    learningKeys: ['commission'],
  },
  {
    key: 'learning_loop',
    label: 'Learning Loop',
    Icon: Brain,
    description:
      'Runs over everything captured — surfaces where your time went, proposes automations, and teaches every agent from real outcomes.',
    daemons: [
      'workers_autonomous_brain',
      'workers_daily_digest',
      'workers_embeddings_backfill',
      'workers_connections_sync',
    ],
    learningKeys: ['learning_loop'],
  },
];

type AgentLearningRow = {
  id: string;
  agent: string;
  title: string;
  source: string | null;
  learned_at: string;
};

// Fetch the whole feed (the table is small — dozens of rows). The page uses
// the full set for accurate per-agent + total counts; the feed itself renders
// only the most recent LEARNING_FEED_LIMIT.
async function fetchLearnings(): Promise<AgentLearningRow[]> {
  const { data, error } = await supabase
    .from('agent_learnings')
    .select('id, agent, title, source, learned_at')
    .order('learned_at', { ascending: false })
    .limit(500);
  if (error) return []; // table may not exist on an older deploy — degrade quietly
  return (data as unknown as AgentLearningRow[]) ?? [];
}

const LEARNING_FEED_LIMIT = 18;

// Rollup precedence: a genuinely-down daemon (alert, ≥3× stale) escalates the
// whole agent to Attention; otherwise ANY actively-beating daemon means the
// agent is working (Healthy) — a single borderline-stale (warning) sibling
// shouldn't downgrade an agent that's clearly doing its job. Degraded is
// reserved for "no daemon alive, some merely stale". null = nothing reporting.
function rollupStatus(statuses: Array<AgentRowStatus | null>): AgentRowStatus | null {
  const present = statuses.filter((s): s is AgentRowStatus => s !== null);
  if (present.length === 0) return null;
  if (present.includes('alert')) return 'alert';
  if (present.includes('alive')) return 'alive';
  if (present.includes('warning')) return 'warning';
  // Before the fallback: 'asleep' (Mac shut) and 'paused' (kill-switch off) are
  // different facts, and the fallback would report the first as the second.
  if (present.includes('asleep')) return 'asleep';
  return 'paused';
}

const ROLLUP_LABEL: Record<AgentRowStatus, string> = {
  alive: 'Healthy',
  warning: 'Degraded',
  alert: 'Attention',
  paused: 'Paused',
  asleep: 'Asleep',
};

function OrchestratorHero({
  online,
  agentCount,
  aliveDaemons,
  totalDaemons,
  skillsLearned,
  tokensToday,
  onOpenPersona,
}: {
  // null = heartbeats still loading → render a neutral "Connecting…" state
  // instead of a misleading red "Offline" on first paint.
  online: boolean | null;
  agentCount: number;
  aliveDaemons: number | null;
  totalDaemons: number | null;
  skillsLearned: number | null;
  tokensToday: number | null;
  onOpenPersona: () => void;
}) {
  const statusDot = online === null ? 'blue' : online ? 'green' : 'red';
  const statusText = online === null ? 'Connecting…' : online ? 'Online' : 'Offline';
  const statusCls = online === null ? 'text-muted-foreground' : online ? 'text-sage' : 'text-alert';
  return (
    <GlassCard hover={false} className="relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{ background: 'radial-gradient(circle at 12% 0%, #2D7D5F 0%, transparent 55%)' }}
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: 'linear-gradient(135deg, #1F4437 0%, #2D7D5F 100%)' }}
          >
            <Cpu className="w-7 h-7 text-primary-foreground" aria-hidden />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-semibold text-foreground">Reverra Orchestrator</h2>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-forest-soft text-forest font-medium">
                Main Agent
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">
              Coordinates every sub-agent, routes tasks across your three businesses, escalates
              failures, and learns from each interaction.
            </p>
            <button
              type="button"
              onClick={onOpenPersona}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-forest hover:underline"
            >
              <Sparkles className="w-3.5 h-3.5" aria-hidden /> View persona
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <PulseDot color={statusDot} size="md" />
          <span className={`text-sm font-medium ${statusCls}`}>{statusText}</span>
        </div>
      </div>
      <div
        className="relative grid grid-cols-2 md:grid-cols-4 gap-6 mt-6 pt-4"
        style={{ borderTop: '1px solid var(--border-soft, rgba(0,0,0,0.08))' }}
      >
        <HeroStat label="Sub-agents managed" value={agentCount.toString()} />
        <HeroStat
          label="Daemons running"
          value={
            aliveDaemons === null || totalDaemons === null ? '—' : `${aliveDaemons}/${totalDaemons}`
          }
          tone={
            aliveDaemons === null || totalDaemons === null
              ? undefined
              : aliveDaemons === totalDaemons
                ? 'sage'
                : aliveDaemons === 0
                  ? 'alert'
                  : undefined
          }
        />
        <HeroStat
          label="Skills learned"
          value={skillsLearned === null ? '—' : skillsLearned.toString()}
          tone="sage"
        />
        <HeroStat
          label="LLM tokens today"
          value={tokensToday === null ? '—' : tokensToday.toLocaleString()}
        />
      </div>
    </GlassCard>
  );
}

function HeroStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'sage' | 'alert';
}) {
  const cls = tone === 'sage' ? 'text-sage' : tone === 'alert' ? 'text-alert' : 'text-foreground';
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-mono font-semibold mt-1 ${cls}`}>{value}</p>
    </div>
  );
}

function SubAgentCard({
  meta,
  daemons,
  learnedCount,
  onOpenPersona,
}: {
  meta: LogicalAgent;
  daemons: Array<{ name: string; status: AgentRowStatus | null; lastSeen: string | null }>;
  learnedCount: number;
  onOpenPersona: () => void;
}) {
  const reporting = daemons.filter((d) => d.status !== null);
  const status = rollupStatus(daemons.map((d) => d.status));
  const tone = status ? STATUS_TONE[status] : IDLE_TONE;
  const aliveCount = daemons.filter((d) => d.status === 'alive').length;
  const lastSeen = daemons
    .map((d) => d.lastSeen)
    .filter((s): s is string => !!s)
    .sort()
    .at(-1);
  const Icon = meta.Icon;

  return (
    <GlassCard
      hover={false}
      className={`transition-all duration-300 ${status === 'alert' ? 'ring-1 ring-alert/30' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-forest-soft flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-forest" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{meta.label}</p>
            <p className="text-[11px] text-muted-foreground">
              {reporting.length}/{daemons.length} daemons
            </p>
          </div>
        </div>
        <span className="flex items-center gap-1.5 shrink-0">
          <PulseDot color={tone.dot} size="sm" />
          <span
            className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${tone.badge}`}
          >
            {status ? ROLLUP_LABEL[status] : 'Idle'}
          </span>
        </span>
      </div>

      <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{meta.description}</p>

      <div
        className="mt-3 pt-3 flex items-center justify-between text-[12px]"
        style={{ borderTop: '1px solid var(--border-soft, rgba(0,0,0,0.06))' }}
      >
        <span className="flex items-center gap-1.5 text-forest font-medium">
          <Sparkles className="w-3.5 h-3.5" aria-hidden />
          {learnedCount} {learnedCount === 1 ? 'skill' : 'skills'} learned
        </span>
        <span className="text-muted-foreground">
          {aliveCount > 0 && lastSeen
            ? `active ${formatDistanceToNowStrict(new Date(lastSeen), { addSuffix: true })}`
            : reporting.length === 0
              ? 'awaiting first beat'
              : 'idle'}
        </span>
      </div>

      <button
        type="button"
        onClick={onOpenPersona}
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-border-soft/60 py-1.5 text-xs font-medium text-forest hover:bg-forest-soft/40 transition-colors"
      >
        <Sparkles className="w-3.5 h-3.5" aria-hidden /> View persona
      </button>
    </GlassCard>
  );
}

function LearningLoopFeed({
  learnings,
  loading,
}: {
  learnings: AgentLearningRow[];
  loading: boolean;
}) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-forest" aria-hidden />
        <h2 className="text-base font-semibold text-foreground">Learning Loop</h2>
        <span className="text-xs text-muted-foreground">— what your agents got better at</span>
      </div>
      <GlassCard hover={false}>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : learnings.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No skills recorded yet. As real data accumulates, each agent records what it learned
            here.
          </p>
        ) : (
          <>
            <ol className="space-y-3">
              {learnings.slice(0, LEARNING_FEED_LIMIT).map((s) => {
                const meta = LEARNING_META[s.agent] ?? { label: s.agent, color: '#5B6172' };
                return (
                  <li key={s.id} className="flex items-start gap-3">
                    <span
                      className="mt-1.5 w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: meta.color }}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground">{s.title}</p>
                      <p className="text-[11px] text-muted-foreground">
                        <span style={{ color: meta.color }}>{meta.label}</span>
                        {s.source ? ` · ${s.source}` : ''} ·{' '}
                        {formatDistanceToNowStrict(new Date(s.learned_at), { addSuffix: true })}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
            {learnings.length > LEARNING_FEED_LIMIT && (
              <p className="mt-3 text-[11px] text-muted-foreground">
                + {learnings.length - LEARNING_FEED_LIMIT} more skills learned
              </p>
            )}
          </>
        )}
      </GlassCard>
    </section>
  );
}

const LEARNING_META: Record<string, { label: string; color: string }> = {
  orchestrator: { label: 'Orchestrator', color: '#1F6F54' },
  email: { label: 'Email Triage', color: '#2D7D5F' },
  meeting: { label: 'Meeting Agent', color: '#4B7A6A' },
  agreement: { label: 'Agreement Agent', color: '#7A5C2E' },
  commission: { label: 'Commission Agent', color: '#9A6A1B' },
  focus: { label: 'Focus Guardian', color: '#3B6E8F' },
  messages: { label: 'Messages Agent', color: '#6A4B7A' },
  learning_loop: { label: 'Learning Loop', color: '#1F6F54' },
};

const Agents = () => {
  const { user } = useAuth();
  // Ticking `now` so status pills decay against wall-clock, not against
  // page-load time. Without this, a daemon that dies 5 minutes after the
  // tab opens keeps classifying as `alive` because the staleness compare
  // uses the original mount-time `now`. 15s cadence is half the
  // heartbeat-poll interval (30s) so a freshly-stopped daemon flips to
  // `warning`/`alert` within one classifier multiplier tick.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(id);
  }, []);

  // retry:0 — RLS permission-denial is deterministic. Retries just hide
  // the real error state behind a "paused" fetchStatus and prevent
  // isError from flipping true. Surface failures immediately so the
  // operator knows app_settings access needs an owner-readable policy.
  const heartbeatsQ = useQuery({
    queryKey: ['heartbeats'],
    queryFn: fetchHeartbeats,
    refetchInterval: 30_000,
    retry: 0,
  });

  const tokenUsageQ = useQuery({
    queryKey: ['heartbeats', 'token_usage_today'],
    queryFn: fetchTodayTokenUsage,
    refetchInterval: 30_000,
    retry: 0,
  });

  // Toggle state — used to decide which daemons render as 'paused' vs
  // their raw heartbeat tier. 10s refetch so a toggle flip in Settings
  // surfaces on /agents within a single poll cycle.
  const togglesQ = useQuery({
    queryKey: ['settings', 'toggle-states'],
    queryFn: fetchToggles,
    refetchInterval: 10_000,
    retry: 0,
  });

  const rows = heartbeatsQ.data ?? [];
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => a.daemon_name.localeCompare(b.daemon_name)),
    [rows],
  );

  const toggleState = useMemo(() => buildToggleStateMap(togglesQ.data ?? []), [togglesQ.data]);

  // Combined status: a paused daemon (kill switch off in Settings)
  // wins over its heartbeat tier so the badge tells the truth about
  // why the daemon isn't doing work. Heartbeat staleness still
  // surfaces via the lower text rows.
  const classified = useMemo(
    () =>
      sortedRows.map((row): { row: HeartbeatRow; status: AgentRowStatus | null } => {
        if (isDaemonPaused(row.daemon_name, toggleState)) {
          return { row, status: 'paused' };
        }
        return {
          row,
          // daemonName so a capture reader on a sleeping Mac reports 'asleep'
          // rather than 'alert'. Without it this page showed "Alert: 2" in red
          // every morning while the sidebar chip — which links HERE — was green.
          status: row.payload
            ? classifyHeartbeat(row.payload, now, { daemonName: row.daemon_name }).status
            : null,
        };
      }),
    [sortedRows, now, toggleState],
  );

  const aliveCount = classified.filter((c) => c.status === 'alive').length;
  const warningCount = classified.filter((c) => c.status === 'warning').length;
  const alertCount = classified.filter((c) => c.status === 'alert').length;
  const pausedCount = classified.filter((c) => c.status === 'paused').length;
  // Separate from pausedCount: 'paused' is a kill-switch the operator turned
  // off, 'asleep' is a capture daemon whose Mac is shut. Folding them together
  // would file "your laptop is closed" under "you disabled this".
  const asleepCount = classified.filter((c) => c.status === 'asleep').length;
  const total = classified.length;

  // Stats panel values: undefined while heartbeats query is loading so
  // PageStatsRow renders an em-dash; numbers once loaded.
  //
  // The red "N daemons in alert state" rollup banner was dropped (it
  // triple-counted the same signal alongside the Alert tile and the
  // per-card red ring). The PageStatsRow stays — it's the at-a-glance
  // numeric anchor; the grid below is the per-daemon detail.
  //
  // Schema-drift rows (payload === null → status === null) are
  // intentionally excluded from all three count tiles. They surface
  // via their dedicated red-bordered DaemonRow card below. As a result
  // `healthy + warning + alert ≤ total` is by design — the "of {total}
  // reporting" copy on the Healthy tile uses the raw row count so a
  // schema-drift row is still acknowledged as "present".
  //
  // Screen field audit:
  //   - Healthy count ← classifyHeartbeat(...).status === 'alive'
  //                    (writer: heartbeat classifier in @reverra/shared)
  //   - Warning count ← classifier === 'warning' (≥1.5× interval stale)
  //   - Alert count   ← classifier === 'alert'   (≥3× interval stale)
  const hasHeartbeatData = heartbeatsQ.isSuccess;

  // Learning Loop feed — the agent_learnings rows each agent recorded as it got
  // better. Same source the Intel page reads (pre-launch: Claude-Code-curated).
  const learningsQ = useQuery({
    queryKey: ['agent_learnings'],
    queryFn: fetchLearnings,
    staleTime: 60_000,
    retry: 0,
  });

  // Infrastructure detail is collapsed by default — the orchestrator + sub-agent
  // narrative leads; the raw per-daemon ops view (restart, kill-switch, schema
  // drift) lives underneath for when something needs hands-on attention.
  const [showInfra, setShowInfra] = useState(false);
  const [personaAgent, setPersonaAgent] = useState<{ key: string; label: string } | null>(null);

  // Roll the per-daemon heartbeat status up into the logical agents.
  const daemonByName = useMemo(() => {
    const m = new Map<string, { status: AgentRowStatus | null; lastSeen: string | null }>();
    for (const { row, status } of classified) {
      m.set(row.daemon_name, { status, lastSeen: row.payload?.last_seen_at ?? null });
    }
    return m;
  }, [classified]);

  const learnedByKey = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of learningsQ.data ?? []) m.set(l.agent, (m.get(l.agent) ?? 0) + 1);
    return m;
  }, [learningsQ.data]);

  const subAgents = useMemo(
    () =>
      LOGICAL_AGENTS.map((meta) => ({
        meta,
        daemons: meta.daemons.map((name) => ({
          name,
          status: daemonByName.get(name)?.status ?? null,
          lastSeen: daemonByName.get(name)?.lastSeen ?? null,
        })),
        learnedCount: meta.learningKeys.reduce((s, k) => s + (learnedByKey.get(k) ?? 0), 0),
      })),
    [daemonByName, learnedByKey],
  );

  const totalSkills = learningsQ.isSuccess ? (learningsQ.data?.length ?? 0) : null;

  // Hero "daemons running" is scoped to the daemons the sub-agent cards cover,
  // so the hero count reconciles with the cards (unmapped daemons — discord,
  // the Mac bridge — live in Infrastructure detail). null while heartbeats load
  // so the hero shows "—" / "Connecting…" instead of a misleading "0/0 Offline".
  const mappedDaemonNames = useMemo(() => new Set(LOGICAL_AGENTS.flatMap((a) => a.daemons)), []);
  const mappedTotal = mappedDaemonNames.size;
  const mappedAlive = useMemo(
    () =>
      classified.filter((c) => mappedDaemonNames.has(c.row.daemon_name) && c.status === 'alive')
        .length,
    [classified, mappedDaemonNames],
  );
  const heroAlive = hasHeartbeatData ? mappedAlive : null;
  const heroTotal = hasHeartbeatData ? mappedTotal : null;
  const orchestratorOnline = hasHeartbeatData ? mappedAlive > 0 : null;

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <h1 className="text-3xl font-serif font-semibold tracking-tight text-foreground flex items-center gap-3">
          <Activity className="w-7 h-7 text-forest" aria-hidden />
          Agents
        </h1>
        <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
          One orchestrator coordinates every sub-agent and gets sharper from each interaction.
        </p>
      </motion.header>

      {heartbeatsQ.isError && (
        <GlassCard hover={false} className="neo-alert">
          <p className="text-sm text-alert">
            Failed to load heartbeats: {(heartbeatsQ.error as Error).message}
          </p>
        </GlassCard>
      )}

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <OrchestratorHero
          online={orchestratorOnline}
          agentCount={LOGICAL_AGENTS.length}
          aliveDaemons={heroAlive}
          totalDaemons={heroTotal}
          skillsLearned={totalSkills}
          tokensToday={tokenUsageQ.data?.used ?? null}
          onOpenPersona={() =>
            setPersonaAgent({ key: 'orchestrator', label: 'Reverra Orchestrator' })
          }
        />
      </motion.div>

      {/* Hierarchy connector */}
      <div className="flex justify-center -my-1" aria-hidden>
        <div className="flex flex-col items-center gap-1 text-muted-foreground/40">
          <div className="w-px h-3 bg-current" />
          <ArrowDown className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-widest">delegates to</span>
        </div>
      </div>

      {/* Sub-agent rollup */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {subAgents.map(({ meta, daemons, learnedCount }) => (
          <SubAgentCard
            key={meta.key}
            meta={meta}
            daemons={daemons}
            learnedCount={learnedCount}
            onOpenPersona={() => setPersonaAgent({ key: meta.key, label: meta.label })}
          />
        ))}
      </div>

      <LearningLoopFeed learnings={learningsQ.data ?? []} loading={learningsQ.isLoading} />

      {/* Infrastructure detail — the raw per-daemon ops view, collapsed by default */}
      <section aria-label="Infrastructure detail">
        <button
          type="button"
          onClick={() => setShowInfra((v) => !v)}
          aria-expanded={showInfra}
          aria-controls="infra-detail-panel"
          className="w-full flex items-center gap-2 rounded-xl border border-border/60 bg-secondary/40 px-4 py-3 hover:bg-secondary/70 transition-colors"
        >
          {showInfra ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
          )}
          <Server className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
          <span className="text-sm font-medium text-foreground">Infrastructure detail</span>
          <span className="ml-auto text-xs text-muted-foreground">
            {hasHeartbeatData ? `${total} daemons · ${aliveCount} healthy` : 'loading…'}
          </span>
        </button>

        {showInfra && (
          <div id="infra-detail-panel" className="mt-4 space-y-4">
            <PageStatsRow
              stats={[
                {
                  label: 'Healthy',
                  value: hasHeartbeatData ? aliveCount : undefined,
                  icon: CheckCircle2,
                  iconClassName: aliveCount > 0 ? 'text-sage' : 'text-muted-foreground',
                  valueClassName: aliveCount > 0 ? 'text-sage' : 'text-foreground',
                  secondary: hasHeartbeatData ? `of ${total} reporting` : undefined,
                },
                {
                  label: 'Paused',
                  value: hasHeartbeatData ? pausedCount : undefined,
                  icon: Pause,
                  iconClassName: 'text-muted-foreground',
                  valueClassName: 'text-foreground',
                  secondary: hasHeartbeatData
                    ? pausedCount === 0
                      ? 'no services off'
                      : 'kill switch disabled'
                    : undefined,
                },
                {
                  label: 'Warning',
                  value: hasHeartbeatData ? warningCount : undefined,
                  icon: Zap,
                  iconClassName: warningCount > 0 ? 'text-attention' : 'text-muted-foreground',
                  valueClassName: warningCount > 0 ? 'text-attention' : 'text-foreground',
                  secondary: hasHeartbeatData
                    ? warningCount === 0
                      ? 'no stale beats'
                      : '≥1.5× interval stale'
                    : undefined,
                },
                {
                  label: 'Asleep',
                  value: hasHeartbeatData ? asleepCount : undefined,
                  icon: Moon,
                  iconClassName: 'text-muted-foreground',
                  valueClassName: 'text-foreground',
                  secondary: hasHeartbeatData
                    ? asleepCount === 0
                      ? 'all machines awake'
                      : 'Mac shut — resumes on wake'
                    : undefined,
                },
                {
                  label: 'Alert',
                  value: hasHeartbeatData ? alertCount : undefined,
                  icon: AlertTriangle,
                  iconClassName: alertCount > 0 ? 'text-alert' : 'text-muted-foreground',
                  valueClassName: alertCount > 0 ? 'text-alert' : 'text-foreground',
                  secondary: hasHeartbeatData
                    ? alertCount === 0
                      ? 'all clear'
                      : '≥3× interval stale'
                    : undefined,
                },
              ]}
            />

            {heartbeatsQ.isLoading && (
              <p className="text-sm text-muted-foreground">Loading heartbeats…</p>
            )}

            {heartbeatsQ.isSuccess && total === 0 && (
              <GlassCard hover={false} className="border-dashed text-center">
                <Cpu className="w-10 h-10 text-muted-foreground/60 mx-auto mb-3" aria-hidden />
                <p className="text-sm text-foreground font-medium">No daemons reporting</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Workers aren't running — start them with{' '}
                  <span className="font-mono text-[11px]">pnpm dev:up</span> and refresh.
                </p>
              </GlassCard>
            )}

            <TokenBudgetCard usage={tokenUsageQ.data} loading={tokenUsageQ.isLoading} />

            {total > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {classified.map(({ row, status }) => (
                  <DaemonRow
                    key={row.daemon_name}
                    row={row}
                    status={status}
                    ownerId={user?.id ?? null}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {personaAgent && (
        <PersonaPanel
          agentKey={personaAgent.key}
          fallbackLabel={personaAgent.label}
          open={!!personaAgent}
          onOpenChange={(o) => {
            if (!o) setPersonaAgent(null);
          }}
        />
      )}
    </div>
  );
};

export function TokenBudgetCard({
  usage,
  loading,
}: {
  usage: TokenUsageRow | undefined;
  loading: boolean;
}) {
  // The cap is a worker-only env var (EMAIL_TRIAGE_DAILY_TOKEN_CAP) and is
  // not currently persisted in app_settings. Until W5 exposes it via a
  // settings row readable from the browser, we render the used count
  // standalone and show "—" for cap/percent. The bar is intentionally
  // omitted in this mode so a user can't misread an unbounded counter
  // as "near zero".
  const used = usage?.used ?? null;
  const hasUsage = used !== null;
  return (
    <GlassCard hover={false}>
      <div className="flex items-center gap-2 mb-2">
        <Database className="w-4 h-4 text-forest" aria-hidden />
        <h2 className="text-sm font-medium text-foreground">Daily LLM budget (shared)</h2>
        <span className="ml-auto text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
          today (UTC)
        </span>
      </div>
      {loading ? (
        <p className="text-xs text-muted-foreground">Loading usage…</p>
      ) : hasUsage ? (
        <>
          <p className="text-2xl font-mono font-semibold text-foreground">
            {used.toLocaleString()}
            <span className="text-sm text-muted-foreground"> / — tokens</span>
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Cap is the EMAIL_TRIAGE_DAILY_TOKEN_CAP env value (worker-side only). Per-daemon
            attribution lands in W5.
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          No token usage recorded for today yet — <span className="font-mono">—</span>
        </p>
      )}
    </GlassCard>
  );
}

// 60s UI-side cooldown so a frustrated operator can't queue 10 restart
// requests in a row before the daemon has acknowledged the first. The
// RLS allows fast UPDATEs, so the gate has to live somewhere — UI is
// the right home for "give the daemon a chance to ack first."
const RESTART_COOLDOWN_MS = 60_000;

const RESTART_REQUEST_KEY_PREFIX = 'daemon_restart_request:';

// localStorage namespace for cross-reload cooldown persistence.
// User feedback (post-R2): the in-memory cooldown reset on page reload,
// letting an operator queue multiple clicks. Persisting to localStorage
// closes that gap.
const RESTART_LS_PREFIX = 'reverra:restart-cooldown:';

function readCooldownFromLocalStorage(daemon: string): number | null {
  try {
    const raw = window.localStorage.getItem(`${RESTART_LS_PREFIX}${daemon}`);
    if (!raw) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    // Private-mode Safari, quota exceeded, etc. — fall through to no-cooldown.
    return null;
  }
}

function writeCooldownToLocalStorage(daemon: string, atMs: number): void {
  try {
    window.localStorage.setItem(`${RESTART_LS_PREFIX}${daemon}`, String(atMs));
  } catch {
    /* private-mode or quota — best-effort */
  }
}

type RestartPayload = {
  requested_at: string;
  requested_by: string | null;
};

async function fetchRestartRequest(daemon: string): Promise<RestartPayload | null> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', `${RESTART_REQUEST_KEY_PREFIX}${daemon}`)
    .maybeSingle();
  if (error || !data?.value) return null;
  try {
    const parsed = JSON.parse(String(data.value));
    if (typeof parsed?.requested_at !== 'string') return null;
    return {
      requested_at: parsed.requested_at,
      requested_by: typeof parsed?.requested_by === 'string' ? parsed.requested_by : null,
    };
  } catch {
    return null;
  }
}

async function writeRestartRequest(daemon: string, ownerId: string | null): Promise<void> {
  const key = `${RESTART_REQUEST_KEY_PREFIX}${daemon}`;
  const value = JSON.stringify({
    requested_at: new Date().toISOString(),
    requested_by: ownerId,
  });
  // Upsert: INSERT on first click, UPDATE on subsequent. Owner has both
  // policies under migration 0032.
  const { error } = await supabase
    .from('app_settings')
    .upsert({ key, value }, { onConflict: 'key' });
  if (error) throw error;
}

function RestartButton({
  daemonName,
  ownerId,
  startedAtIso,
  status,
}: {
  daemonName: string;
  ownerId: string | null;
  /** Heartbeat's started_at. When this advances past the click time,
   *  the daemon has acknowledged the restart and the spinner clears. */
  startedAtIso: string | null;
  /** Daemon health tier — drives the stale-aware tooltip for daemons
   *  that the restart can't actually help (process not running). */
  status: AgentRowStatus | null;
}): JSX.Element {
  const qc = useQueryClient();
  const queryKey = ['restart-request', daemonName] as const;

  // Track last-click time in BOTH component state AND localStorage:
  // - component state covers the within-session 60s gating
  // - localStorage persists across page reloads so an operator can't
  //   bypass cooldown by hitting refresh
  // Initial state seeds from localStorage so the cooldown is honored
  // immediately on mount.
  const [lastClickedAtMs, setLastClickedAtMs] = useState<number | null>(() =>
    typeof window !== 'undefined' ? readCooldownFromLocalStorage(daemonName) : null,
  );

  const q = useQuery({
    queryKey,
    queryFn: () => fetchRestartRequest(daemonName),
    refetchInterval: 5_000,
    retry: 0,
  });

  const mut = useMutation({
    mutationFn: () => writeRestartRequest(daemonName, ownerId),
    onMutate: () => {
      // Stamp BEFORE the network round-trip so a slow Supabase
      // doesn't let a double-click squeeze through. Persist to
      // localStorage so a page reload mid-cooldown doesn't reset us.
      const now = Date.now();
      setLastClickedAtMs(now);
      writeCooldownToLocalStorage(daemonName, now);
    },
    onError: (err) => {
      toast.error('Restart request failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    },
    onSuccess: () => {
      toast(`${humanizeDaemonName(daemonName)} — restart requested`, {
        description:
          'The daemon will respawn on its next tick (≤60s). Soft restart — no process kill.',
        duration: 6000,
      });
      qc.invalidateQueries({ queryKey });
    },
  });

  // Three signals decide button state:
  //
  //   serverRequestedAt — the restart-request row exists in app_settings
  //                       (set by writeRestartRequest, cleared by the
  //                       workers central processor).
  //   heartbeatAdvanced — the daemon's heartbeat's started_at is now
  //                       after our last click — proof the daemon
  //                       respawned. This is the honest ack: for bridge
  //                       daemons it ticks within ~30s; for in-process
  //                       force-ack daemons it stays at the original
  //                       boot time and the cooldown wins instead.
  //   onCooldown        — 60s window since the last click (localStorage
  //                       persists across reloads). Defends against an
  //                       impatient operator re-clicking 5 times.
  //
  // Spinner shows while server-row is present OR while we're still
  // waiting for heartbeat to advance.
  const serverRequestedAt = q.data?.requested_at ? new Date(q.data.requested_at).getTime() : null;
  const heartbeatStartedAt = startedAtIso ? new Date(startedAtIso).getTime() : null;
  const heartbeatAdvanced =
    lastClickedAtMs !== null && heartbeatStartedAt !== null && heartbeatStartedAt > lastClickedAtMs;
  const cooldownStartMs = Math.max(serverRequestedAt ?? 0, lastClickedAtMs ?? 0) || null;
  const cooldownRemainingMs =
    cooldownStartMs !== null
      ? Math.max(0, RESTART_COOLDOWN_MS - (Date.now() - cooldownStartMs))
      : 0;
  const onCooldown = cooldownRemainingMs > 0;
  // Pending = either the server-row is present (bridge case: processor
  // hasn't deleted it yet) OR we're still inside cooldown AND the
  // heartbeat hasn't advanced. The second clause covers the in-process
  // force-ack daemons whose started_at never changes: after 60s we
  // stop spinning and treat the cooldown expiry as the ack.
  const isPending = serverRequestedAt !== null || (onCooldown && !heartbeatAdvanced);

  // Stale-aware tooltip: when a daemon is in `alert` (≥3× interval
  // stale), the most likely cause is "process not running" and a
  // soft restart can still help only if the parent (workers)
  // process is alive — which is when the central processor can act.
  // The button stays clickable but we tell the operator honestly.
  const isStale = status === 'alert' || status === 'warning';
  const isPaused = status === 'paused';

  const disabled = mut.isPending || onCooldown || isPending;
  const tooltip = onCooldown
    ? `Cooldown — ${Math.ceil(cooldownRemainingMs / 1000)}s remaining`
    : isPending
      ? 'Restart in progress — waiting for daemon to respawn…'
      : isPaused
        ? 'Paused via Settings → Connected Services. Re-enable the toggle there before restarting.'
        : isStale
          ? "Restart this daemon. If the workers process is also stopped, this can't help — start workers from your terminal first."
          : 'Restart this daemon (soft — no process kill)';

  return (
    <button
      type="button"
      title={tooltip}
      aria-label={`Restart ${humanizeDaemonName(daemonName)}`}
      disabled={disabled}
      onClick={() => mut.mutate()}
      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {mut.isPending || isPending ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <RefreshCw className="w-3.5 h-3.5" />
      )}
    </button>
  );
}

export function DaemonRow({
  row,
  status,
  ownerId = null,
}: {
  row: HeartbeatRow;
  status: AgentRowStatus | null;
  /** Owner id from the auth context. NULL until the auth bootstrap
   *  resolves — the restart button stays disabled in that window. */
  ownerId?: string | null;
}) {
  const [errorOpen, setErrorOpen] = useState(false);
  const label = humanizeDaemonName(row.daemon_name);

  if (!row.payload) {
    return (
      <GlassCard hover={false} className="neo-alert">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-alert" aria-hidden />
          <span className="text-sm font-medium text-foreground">{label}</span>
          <Badge variant="destructive" className="ml-auto">
            Schema drift
          </Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground font-mono">{row.parse_error}</p>
      </GlassCard>
    );
  }

  const payload: HeartbeatPayload = row.payload;
  const tone = STATUS_TONE[status ?? 'alive'];
  const hasError = !!payload.last_error;

  return (
    <GlassCard hover={false}>
      {/*
        Title row: pulse dot + humanized name (flex-1) + status pill
        right-aligned. The snake_case daemon_name no longer lives here;
        it moved to its own labeled "ID:" row in the metadata block so
        the title stays scannable at a glance.
      */}
      <div className="flex items-center gap-3">
        <PulseDot color={tone.dot} />
        <span className="text-sm font-medium text-foreground truncate flex-1">{label}</span>
        <RestartButton
          daemonName={row.daemon_name}
          ownerId={ownerId}
          startedAtIso={payload.started_at}
          status={status}
        />
        <span
          className={`text-[10px] uppercase tracking-wide px-2.5 py-1 rounded-full ${tone.badge} shrink-0`}
        >
          {status ?? 'unknown'}
        </span>
      </div>

      {/*
        Metadata block — vertical labeled rows. Each row: icon + label +
        value. Icon-led for scannability; the colon-separated label keeps
        the row self-describing (a screen-reader-only span would also
        work, but the visible label is clearer at small text sizes and
        matches the design reference).
        Screen-field-audit:
          - "Last beat" ← payload.last_seen_at (writer: startHeartbeat tick)
          - "Host"      ← payload.host         (writer: startHeartbeat init)
          - "Uptime"    ← payload.started_at   (writer: startHeartbeat init)
          - "Ticks"     ← payload.tick_count   (writer: startHeartbeat tick)
          - "ID"        ← row.daemon_name      (writer: app_settings key suffix)
      */}
      <div className="mt-3 space-y-1.5 text-[12px]">
        <div className="flex items-center gap-2">
          <HeartPulse
            className={`w-3.5 h-3.5 shrink-0 ${tone.dot === 'green' ? 'text-forest' : tone.dot === 'amber' ? 'text-attention' : 'text-alert'}`}
            aria-label="last heartbeat"
          />
          <span className="text-muted-foreground">Last beat:</span>
          <span className="font-medium text-foreground">
            {formatDistanceToNowStrict(new Date(payload.last_seen_at), { addSuffix: true })}
          </span>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <Monitor className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" aria-label="host" />
          <span className="text-muted-foreground">Host:</span>
          <span className="font-mono text-foreground/90 truncate">{payload.host}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TrendingUp
            className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0"
            aria-label="uptime"
          />
          <span className="text-muted-foreground">Uptime:</span>
          <span className="font-medium text-foreground">
            {formatDistanceToNowStrict(new Date(payload.started_at))}
          </span>
          <span className="text-muted-foreground/40" aria-hidden>
            |
          </span>
          <span className="text-muted-foreground">Ticks:</span>
          <span className="font-medium text-foreground font-mono">
            {payload.tick_count.toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <Tag className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" aria-label="daemon id" />
          <span className="text-muted-foreground">ID:</span>
          <span className="font-mono text-[11px] text-muted-foreground/80 truncate">
            {row.daemon_name}
          </span>
        </div>
      </div>

      {hasError && (
        /*
          Diagnostic Log section. Mockup pattern: tinted toggle header
          + tinted snippet block. We use our attention tone (amber) so
          it harmonizes with the rest of the design system; the mockup's
          warm tan/orange is intentionally not adopted (user direction).
        */
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setErrorOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 rounded-md bg-attention/10 border border-attention/30 px-3 py-2 hover:bg-attention/15 transition-colors"
            aria-expanded={errorOpen}
          >
            <span className="flex items-center gap-2 min-w-0">
              {errorOpen ? (
                <ChevronDown className="w-3.5 h-3.5 text-attention shrink-0" aria-hidden />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 text-attention shrink-0" aria-hidden />
              )}
              <span className="text-[11px] uppercase tracking-wide font-semibold text-attention">
                Diagnostic log
              </span>
              {payload.last_error_at && (
                <span className="text-[11px] text-muted-foreground truncate">
                  (recent error:{' '}
                  {formatDistanceToNowStrict(new Date(payload.last_error_at), { addSuffix: true })})
                </span>
              )}
            </span>
            <AlertTriangle className="w-3.5 h-3.5 text-attention shrink-0" aria-hidden />
          </button>
          {errorOpen && (
            <pre className="mt-1 rounded-md bg-attention/5 border border-attention/20 px-3 py-2 text-[11px] font-mono text-foreground whitespace-pre-wrap break-words max-h-40 overflow-auto">
              <span className="text-attention font-sans not-italic font-semibold">Snippet: </span>
              {payload.last_error}
            </pre>
          )}
        </div>
      )}
    </GlassCard>
  );
}

export default Agents;
